import { Checkbox, FormControlLabel } from "@material-ui/core";
import React, { FC } from "react";
import styled from "styled-components";
import { Spinner } from "../../../components/Spinner";
import { SwitchInput } from "../../../components/SwitchField";
import {
  OrderStatus,
  OrderStatusPreferencesInput,
  RequisitionStatus,
  RequisitionStatusPreferencesInput,
} from "../../../generated/graphql";
import { orderStatusLabelByStatus } from "../../orders/constants/orderStatus";
import { statusLabelByStatus as requisitionStatusLabelByStatus } from "../../requisitions/constants/requisitionStatus";
import { useNotificationPreferences } from "../../../hooks/bff/useNotificationPreferences";
import useTranslateMessage from "../../../hooks/useTranslateMessage";
import { styledSpace } from "../../../styles/theme";
import { useSnackbar } from "../../snackbars";

// Reuse the Orders/Requisitions pages' own translation IDs rather than a
// second, hand-copied set of labels - that duplication is exactly what
// let this drift out of sync with mobile's real wording once already.
const REQUISITION_STATUS_FIELDS: {
  field: keyof RequisitionStatusPreferencesInput;
  translationId: string;
}[] = [
  {
    field: "pendingApproval",
    translationId:
      requisitionStatusLabelByStatus[RequisitionStatus.PendingApproval],
  },
  {
    field: "approved",
    translationId: requisitionStatusLabelByStatus[RequisitionStatus.Approved],
  },
  {
    field: "rejected",
    translationId: requisitionStatusLabelByStatus[RequisitionStatus.Rejected],
  },
  {
    field: "closed",
    translationId: requisitionStatusLabelByStatus[RequisitionStatus.Closed],
  },
];

const ORDER_STATUS_FIELDS: {
  field: keyof OrderStatusPreferencesInput;
  translationId: string;
}[] = [
  {
    field: "draft",
    translationId: orderStatusLabelByStatus[OrderStatus.Draft],
  },
  {
    field: "inRevision",
    translationId: orderStatusLabelByStatus[OrderStatus.InRevision],
  },
  {
    field: "pendingApproval",
    translationId: orderStatusLabelByStatus[OrderStatus.PendingApproval],
  },
  {
    field: "approved",
    translationId: orderStatusLabelByStatus[OrderStatus.Approved],
  },
  {
    field: "awaitingDelivery",
    translationId: orderStatusLabelByStatus[OrderStatus.AwaitingDelivery],
  },
  {
    field: "delivered",
    translationId: orderStatusLabelByStatus[OrderStatus.Delivered],
  },
];

// Single source of truth for each group's field set - REQUISITION/ORDER_STATUS_FIELDS
// above drive both the rendered checkboxes and these all-on/all-off shortcuts, so a
// status added to one automatically reaches the master toggle too.
function allStatuses<T extends Record<string, boolean>>(
  fields: { field: keyof T }[],
  value: boolean,
): T {
  return Object.fromEntries(fields.map(({ field }) => [field, value])) as T;
}

const ALL_OFF_REQUISITION_STATUSES =
  allStatuses<RequisitionStatusPreferencesInput>(
    REQUISITION_STATUS_FIELDS,
    false,
  );
const ALL_ON_REQUISITION_STATUSES =
  allStatuses<RequisitionStatusPreferencesInput>(
    REQUISITION_STATUS_FIELDS,
    true,
  );
const ALL_OFF_ORDER_STATUSES = allStatuses<OrderStatusPreferencesInput>(
  ORDER_STATUS_FIELDS,
  false,
);
const ALL_ON_ORDER_STATUSES = allStatuses<OrderStatusPreferencesInput>(
  ORDER_STATUS_FIELDS,
  true,
);

type PreferencesState = {
  requisitionStatuses: RequisitionStatusPreferencesInput;
  orderStatuses: OrderStatusPreferencesInput;
};

const Container = styled.div`
  padding: 0 28px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EmailSwitch = styled(SwitchInput)`
  font-family: "Assistant", sans-serif;

  .SwitchInput__label {
    margin: 0;
    gap: 8px;
  }

  .MuiFormControlLabel-label {
    font-family: "Assistant", sans-serif;
    font-size: 13px;
    font-weight: 600;
  }

  .MuiSwitch-root {
    direction: ltr;
    width: 30px;
    height: 16px;
    flex: 0 0 30px;
    padding: 0;
    overflow: hidden;
  }

  .MuiSwitch-switchBase {
    inset-inline-start: 0 !important;
    inset-inline-end: auto !important;
    padding: 2px;
    transform: translateX(0) !important;
    color: #ffffff;
  }

  .MuiSwitch-thumb {
    width: 12px;
    height: 12px;
    box-shadow: none;
  }

  .MuiSwitch-track {
    border-radius: 8px;
    opacity: 1;
    background-color: #d8d8d8;
  }

  .MuiSwitch-switchBase.Mui-checked {
    transform: translateX(14px) !important;
    color: #ffffff;
  }

  .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
    opacity: 1;
    background-color: #ffd400;
  }
`;

const Hint = styled.p`
  margin: 0 0 20px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey};
`;

const StatusGroupsCard = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.colors.lightgrey}4d;
  border-radius: 14px;
`;

// Adjacent-sibling border, not a border on every group, so two groups in the
// same card get a single divider between them rather than each having its
// own boxed card (matches the mock's single card wrapping both groups).
const StatusGroupWrapper = styled.div`
  & + & {
    margin-block-start: 16px;
    padding-block-start: 16px;
    border-block-start: 1px solid ${({ theme }) => theme.colors.lightgrey};
  }
`;

const StatusGroupTitle = styled.h4`
  font-size: 13px;
  font-weight: 700;
  margin-block-end: 4px;
`;

const StatusGroupGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 16px;
  margin-block-start: 12px;

  .MuiFormControlLabel-root {
    min-height: 28px;
    margin: 0;
    gap: 6px;
  }

  .MuiCheckbox-root {
    padding: 3px;
  }

  .MuiFormControlLabel-label {
    font-family: "Assistant", sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.2;
  }
`;

const StatusCheckboxIcon = styled.span<{ $checked?: boolean }>`
  position: relative;
  width: 14px;
  height: 14px;
  box-sizing: border-box;
  border: 1px solid ${({ $checked }) => ($checked ? "#ffd400" : "#bfc3c7")};
  border-radius: 2px;
  background: ${({ $checked }) => ($checked ? "#ffd400" : "#ffffff")};

  ${({ $checked }) =>
    $checked &&
    `
      &::after {
        content: "";
        position: absolute;
        inset-block-start: 1px;
        inset-inline-start: 4px;
        width: 4px;
        height: 7px;
        border: solid #303030;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    `}
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${styledSpace(8)} 0;
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${styledSpace(2)};
  padding: ${styledSpace(8)} 0;
  text-align: center;
`;

const RetryButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: #ffd400;
  font-family: "Assistant", sans-serif;
  font-size: 13px;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
`;

interface StatusGroupProps<T extends Record<string, boolean>> {
  testIdPrefix: string;
  title: string;
  hint: string;
  fields: { field: keyof T; translationId: string }[];
  values: T;
  onChangeOne: (field: keyof T, value: boolean) => void;
}

function StatusGroup<T extends Record<string, boolean>>({
  testIdPrefix,
  title,
  hint,
  fields,
  values,
  onChangeOne,
}: StatusGroupProps<T>) {
  const t = useTranslateMessage();

  return (
    <StatusGroupWrapper>
      <StatusGroupTitle>{title}</StatusGroupTitle>
      <Hint>{hint}</Hint>
      <StatusGroupGrid>
        {fields.map(({ field, translationId }) => (
          <FormControlLabel
            key={String(field)}
            control={
              <Checkbox
                icon={<StatusCheckboxIcon />}
                checkedIcon={<StatusCheckboxIcon $checked />}
                checked={values[field]}
                onChange={(_, checked) => onChangeOne(field, checked)}
                data-cy={`notification-preferences-${testIdPrefix}-${String(
                  field,
                )}`}
              />
            }
            label={t({ id: translationId })}
          />
        ))}
      </StatusGroupGrid>
    </StatusGroupWrapper>
  );
}

const NotificationsSection: FC = () => {
  const t = useTranslateMessage();
  const { createErrorSnackbar } = useSnackbar();
  const { isOrderModerator, data, loading, error, updatePreferences, refetch } =
    useNotificationPreferences();

  // Local state, keyed off the query result rather than derived on every
  // render, so an in-flight write's optimistic value isn't clobbered by a
  // stale re-render before the mutation resolves.
  const [state, setState] = React.useState<PreferencesState | undefined>(
    undefined,
  );

  React.useEffect(() => {
    if (data) {
      const { __typename: _requisitionTypename, ...requisitionStatuses } =
        data.requisitionStatuses;
      const { __typename: _orderTypename, ...orderStatuses } =
        data.orderStatuses;
      setState({ requisitionStatuses, orderStatuses });
    }
  }, [data]);

  // Only OrderModerators can register for order/requisition status
  // notifications - matches the bffv2 authorization rule and mobile's MVP
  // scope for this feature.
  if (!isOrderModerator) {
    return null;
  }

  if (!state) {
    if (loading) {
      return (
        <LoadingWrapper>
          <Spinner size={24} />
        </LoadingWrapper>
      );
    }

    if (error) {
      return (
        <ErrorWrapper>
          <Hint>{t({ id: "notificationPreferences-loadFailed" })}</Hint>
          <RetryButton onClick={() => refetch()}>
            {t({ id: "notificationPreferences-retry" })}
          </RetryButton>
        </ErrorWrapper>
      );
    }

    return null;
  }

  // On failure, refetch server truth rather than reverting to a snapshot
  // taken when this particular change started: with several toggles firing
  // in quick succession (each is its own immediate write, chained through
  // the hook's pendingRef so the server sees them in order), a snapshot
  // taken before an earlier toggle would clobber a later toggle's already-
  // applied change if we reverted to it locally. Refetching always
  // reconciles the UI to whatever the server actually has.
  const applyChange = (next: PreferencesState) => {
    setState(next);
    updatePreferences(next).catch(() => {
      createErrorSnackbar(t({ id: "notificationPreferences-saveFailed" }))();
      refetch();
    });
  };

  const emailOn =
    REQUISITION_STATUS_FIELDS.some(
      ({ field }) => state.requisitionStatuses[field],
    ) || ORDER_STATUS_FIELDS.some(({ field }) => state.orderStatuses[field]);

  return (
    <Container>
      <ToggleRow>
        <EmailSwitch
          value={emailOn}
          onChange={(value) =>
            applyChange({
              requisitionStatuses: value
                ? ALL_ON_REQUISITION_STATUSES
                : ALL_OFF_REQUISITION_STATUSES,
              orderStatuses: value
                ? ALL_ON_ORDER_STATUSES
                : ALL_OFF_ORDER_STATUSES,
            })
          }
        >
          {t({ id: "notificationPreferences-emailToggle" })}
        </EmailSwitch>
      </ToggleRow>
      <Hint>{t({ id: "notificationPreferences-emailToggleDescription" })}</Hint>

      {emailOn && (
        <StatusGroupsCard>
          <StatusGroup
            testIdPrefix="requisition"
            title={t({
              id: "notificationPreferences-requisitionStatusTitle",
            })}
            hint={t({ id: "notificationPreferences-chooseStatusesHint" })}
            fields={REQUISITION_STATUS_FIELDS}
            values={state.requisitionStatuses}
            onChangeOne={(field, value) =>
              applyChange({
                ...state,
                requisitionStatuses: {
                  ...state.requisitionStatuses,
                  [field]: value,
                },
              })
            }
          />
          <StatusGroup
            testIdPrefix="order"
            title={t({ id: "notificationPreferences-orderStatusTitle" })}
            hint={t({ id: "notificationPreferences-chooseStatusesHint" })}
            fields={ORDER_STATUS_FIELDS}
            values={state.orderStatuses}
            onChangeOne={(field, value) =>
              applyChange({
                ...state,
                orderStatuses: { ...state.orderStatuses, [field]: value },
              })
            }
          />
        </StatusGroupsCard>
      )}
    </Container>
  );
};

export { NotificationsSection };
